import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Modal } from 'react-bootstrap'
import AppLoader from '../common/AppLoader';
import MDEditor from '@uiw/react-md-editor';
import { BdaService } from '../../models/BdaService';
import { Services } from '../../api/Services';
import { useToasts } from '../../contexts/toasts';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';

type Props = {
  service?: BdaService;
  show?: boolean;
  onClose?: VoidFunction;
}

export default function ServiceMarkdownModal({ service, show, onClose }: Props) {
  const [markdown, setMarkdown] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const serviceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "services"));
  const { showToast } = useToasts();

  const getMarkdown = useCallback(() => {
    setLoading(true);

    Services.getServiceMarkDown(service!.id as number)
      .then(
        ({ data }) => setMarkdown(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('service-markdown-error', message, `${serviceLabels?.singular} Documentation Error ${status ? `(${status})` : ''}`, 'danger');
        }
      ).then(() => setLoading(false));
    // eslint-disable-next-line
  }, [service, showToast]);

  useEffect(() => {
    if (show && service) getMarkdown();
  }, [show, service, getMarkdown]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-circle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    )

    if (loading) return <AppLoader justify="center" size={80} />;

    return markdown
      ? (
        <div data-color-mode="light">
          <MDEditor.Markdown source={markdown} />
        </div>
      )
      : (
        <Alert variant="info" className="py-2 text-center">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No markdown found for this {serviceLabels?.singular || "Service"}.</span>
        </Alert>
      )
  }, [error, loading, markdown, serviceLabels]);

  return (
    <Modal show={show} size="lg" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="text-capitalize">'{service?.name}' Documentation</Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderAsyncContent()}</Modal.Body>
    </Modal>
  )
}